import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { IProps } from './index.types';

import {
  Body,
  Container,
  CompactButtonsContainer,
  CompactInfoRow,
  CompactInfoText,
  CompactStatusPill,
  GeneralInfoAddressContainer,
  GeneralInfoAddressText,
  GeneralInfoSection,
  GeneralInfoHeader,
  GeneralInfoDateContainer,
  GeneralInfoDateStatusText,
  GeneralInfoDateText,
  GeneralInfoContainer,
  DataSection,
  DataOccurrenceNumber,
  DataItemContainer,
  DataItemTitle,
  DataItemInfoText,
  DataItemsContainer,
  DataItemsWoundedAndDeadContainer,
  OccurrenceDateInfo,
  OccurrenceDateContainer,
  ButtonsContainer,
  ConfirmButton,
  BackButton,
  ActionsSection,
  ClearButton
} from './index.styled';

import alertDark from '../../../../../../../assets/alertDark.png';
import alertRed from '../../../../../../../assets/redAlert2.png';
import localWhite from '../../../../../../../assets/localIcon2.png';
import agenteWhite from '../../../../../../../assets/policeIcon2.png';
import personWhite from '../../../../../../../assets/personIcon2.png';
import timePostIcon from '../../../../../../../assets/timePost.svg';
import { Divider } from '../../../../../../../shared/styles';
import { OccurrenceContext } from '../../../../../providers/OccurrenceProvider';
import { EOccurrenceStatus } from '../../../../../../../shared/enums/ocurrenceStatus';
import { ERequestStatus } from '../../../../../../../shared/enums/requestStatus';
import Loading from '../../../../../../../components/Loading';

const CreateEditOccurrenceHeader: React.FC<IProps> = ({
  newOccurrence = false,
  onConfirm,
  onBack,
  onClear,
  onOpenChanges,
  withSidebar = false
}) => {
  const { createEditOccurrenceState } = useContext(OccurrenceContext);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const [isCompact, setIsCompact] = useState(false);

  const formatedWoundedAndDeadInfo = (wounded: number, dead: number) =>
    `${wounded} - feridos / ${dead} - morte`;

  const dataCy = (value: string) => `createEditOccurrenceHeader-${value}`;

  const getUserName = () => {
    if (createEditOccurrenceState?.editableOccurrenceData?.user) {
      return (
        createEditOccurrenceState?.editableOccurrenceData?.user?.name ||
        createEditOccurrenceState?.editableOccurrenceData?.user?.nickname ||
        'Desconhecido'
      );
    }
    return 'Moderador';
  };

  const isForApproval = () => {
    if (createEditOccurrenceState?.editableOccurrenceData?.status) {
      return (
        createEditOccurrenceState?.editableOccurrenceData?.status ===
        EOccurrenceStatus.Waiting
      );
    }
    return false;
  };

  const isLoading = () => {
    return (
      createEditOccurrenceState.editOccurrenceRequestStatus.status ===
        ERequestStatus.Loading ||
      createEditOccurrenceState.createOccurrenceRequestStatus.status ===
        ERequestStatus.Loading
    );
  };

  const getButtonLabel = () => {
    if (newOccurrence) {
      return 'Finalizar registro';
    } else if (isForApproval()) {
      return 'Finalizar aprovação';
    } else {
      return 'Finalizar edição';
    }
  };
  const backButtonLabel = newOccurrence ? 'Salvar rascunho e voltar' : 'Voltar';

  const hasShot = createEditOccurrenceState.victimsCount.total > 0;
  const formattedDate = !createEditOccurrenceState.detailsForm.date
    ? '--/--/---- --:--'
    : new Date(createEditOccurrenceState.detailsForm.date).toLocaleString(
        'pt-BR'
      );
  const addressText = createEditOccurrenceState.detailsForm.address || '---';
  const documentNumber =
    createEditOccurrenceState?.editableOccurrenceData?.documentNumber;
  const occurrenceNumberText = newOccurrence
    ? 'Nova ocorrência'
    : `Ocorrência #${documentNumber || '---'}`;
  const statusText = hasShot ? 'Tiros com baleados' : 'Tiros sem baleados';
  const compactInfo = `${occurrenceNumberText} | ${formattedDate} | ${addressText}`;
  const confirmButtonContent = isLoading() ? (
    <>
      <Loading size="sm" />
      Processando...
    </>
  ) : (
    getButtonLabel()
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    lastScrollYRef.current = window.scrollY;

    const collapseOffset = 120;
    const expandDelta = 24;

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const lastScroll = lastScrollYRef.current;
      const scrollingDown = currentScroll > lastScroll;

      setIsCompact(previous => {
        if (scrollingDown && currentScroll > collapseOffset && !previous) {
          return true;
        }

        if (
          !scrollingDown &&
          lastScroll - currentScroll > expandDelta &&
          previous
        ) {
          return false;
        }

        if (currentScroll <= collapseOffset && previous) {
          return false;
        }

        return previous;
      });

      lastScrollYRef.current = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !headerRef.current) return;

    const updateHeaderOffset = () => {
      if (!headerRef.current) return;

      document.documentElement.style.setProperty(
        '--occurrence-header-offset',
        `${headerRef.current.offsetHeight}px`
      );
    };

    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    return () => {
      window.removeEventListener('resize', updateHeaderOffset);
    };
  }, [isCompact, withSidebar]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    return () => {
      document.documentElement.style.removeProperty(
        '--occurrence-header-offset'
      );
    };
  }, []);

  return (
    <Body ref={headerRef} $isCompact={isCompact}>
      <Container $isCompact={isCompact} $withSidebar={withSidebar}>
        {isCompact ? (
          <CompactInfoRow>
            <CompactStatusPill $withShot={hasShot}>
              {statusText}
            </CompactStatusPill>
            <CompactInfoText title={compactInfo}>{compactInfo}</CompactInfoText>
            <CompactButtonsContainer>
              <ConfirmButton
                data-cy={dataCy('confirmButton')}
                onClick={onConfirm}
                newOccurrence={newOccurrence}
                disabled={isLoading()}
                $compact
              >
                {confirmButtonContent}
              </ConfirmButton>
              {newOccurrence && onClear ? (
                <ClearButton onClick={onClear} $compact>
                  Limpar formulário
                </ClearButton>
              ) : null}
              {!newOccurrence && onOpenChanges ? (
                <BackButton onClick={onOpenChanges} $compact>
                  <img src={timePostIcon} width={16} height={16} alt="" />
                  Alterações
                </BackButton>
              ) : null}
              <BackButton onClick={onBack} $compact>
                {backButtonLabel}
              </BackButton>
            </CompactButtonsContainer>
          </CompactInfoRow>
        ) : (
          <>
            <GeneralInfoSection>
              <GeneralInfoHeader withShot={hasShot}>
                Registrado por<b>{getUserName()}</b>
              </GeneralInfoHeader>
              <GeneralInfoContainer>
                <GeneralInfoDateContainer>
                  <img
                    src={hasShot ? alertRed : alertDark}
                    width={28}
                    height={28}
                  />
                  <Divider width={12} />
                  <div>
                    <GeneralInfoDateStatusText withShot={hasShot}>
                      {statusText}
                    </GeneralInfoDateStatusText>
                    <GeneralInfoDateText>{formattedDate}</GeneralInfoDateText>
                  </div>
                </GeneralInfoDateContainer>
                <Divider height={12} />
                <GeneralInfoAddressContainer>
                  <img src={localWhite} width={28} height={28} />
                  <Divider width={12} />
                  <GeneralInfoAddressText>{addressText}</GeneralInfoAddressText>
                </GeneralInfoAddressContainer>
              </GeneralInfoContainer>
            </GeneralInfoSection>

            <DataSection>
              <DataOccurrenceNumber>
                #{!newOccurrence ? documentNumber : <></>}
              </DataOccurrenceNumber>

              <DataItemsContainer>
                <DataItemsWoundedAndDeadContainer>
                  <DataItem
                    title="Civis"
                    info={formatedWoundedAndDeadInfo(
                      createEditOccurrenceState.victimsCount.civilWounded,
                      createEditOccurrenceState.victimsCount.civilDead
                    )}
                    icon={personWhite}
                  />
                  <Divider height={12} />
                  <DataItem
                    title="Agentes de segurança"
                    info={formatedWoundedAndDeadInfo(
                      createEditOccurrenceState.victimsCount.agentWounded,
                      createEditOccurrenceState.victimsCount.agentDead
                    )}
                    icon={agenteWhite}
                  />
                </DataItemsWoundedAndDeadContainer>
                <Divider width={32} />
                <div
                  style={{
                    maxWidth: '80%',
                    overflow: 'auto'
                  }}
                >
                  <DataItem
                    title="Descrição da ocorrência"
                    info={
                      !createEditOccurrenceState.detailsForm.description
                        ? '---'
                        : createEditOccurrenceState.detailsForm.description
                    }
                  />
                </div>
              </DataItemsContainer>
            </DataSection>

            <ActionsSection newOccurrence={newOccurrence}>
              {!newOccurrence ? (
                <OccurrenceDateContainer>
                  {createEditOccurrenceState.editableOccurrenceData
                    ?.createdAt ? (
                    <OccurrenceDateInfo>
                      Data de cadastro:{' '}
                      <b style={{ fontSize: 12 }}>
                        {createEditOccurrenceState.editableOccurrenceData?.createdAt?.toLocaleString(
                          'pt-BR'
                        )}
                      </b>
                    </OccurrenceDateInfo>
                  ) : (
                    <></>
                  )}

                  {createEditOccurrenceState.editableOccurrenceData
                    ?.createdAt ? (
                    <OccurrenceDateInfo>
                      Última atualização:{' '}
                      <b style={{ fontSize: 12 }}>
                        {' '}
                        {createEditOccurrenceState.editableOccurrenceData?.updatedAt?.toLocaleString(
                          'pt-BR'
                        )}
                      </b>
                    </OccurrenceDateInfo>
                  ) : (
                    <> </>
                  )}
                </OccurrenceDateContainer>
              ) : (
                <></>
              )}

              <ButtonsContainer>
                <ConfirmButton
                  data-cy={dataCy('confirmButton')}
                  onClick={onConfirm}
                  newOccurrence={newOccurrence}
                  disabled={isLoading()}
                >
                  {confirmButtonContent}
                </ConfirmButton>
                <Divider height={12} />
                {newOccurrence && onClear ? (
                  <>
                    <ClearButton onClick={onClear}>
                      Limpar formulário
                    </ClearButton>
                    <Divider height={12} />
                  </>
                ) : null}
                {!newOccurrence && onOpenChanges ? (
                  <>
                    <BackButton onClick={onOpenChanges}>
                      <img src={timePostIcon} width={16} height={16} alt="" />
                      Alterações
                    </BackButton>
                    <Divider height={12} />
                  </>
                ) : null}
                <BackButton onClick={onBack}>{backButtonLabel}</BackButton>
              </ButtonsContainer>
            </ActionsSection>
          </>
        )}
      </Container>
    </Body>
  );
};

const DataItem: React.FC<{
  title: string;
  info: string;
  icon?: string;
}> = ({ title, info, icon }) => {
  return (
    <DataItemContainer>
      <DataItemTitle>{title}</DataItemTitle>
      <Divider height={8} />
      <div style={{ display: 'flex' }}>
        {icon ? (
          <>
            <img src={icon} width={28} height={28} />
            <Divider width={12} />
          </>
        ) : (
          <></>
        )}

        <DataItemInfoText>{info}</DataItemInfoText>
      </div>
    </DataItemContainer>
  );
};

export default CreateEditOccurrenceHeader;
